import { Money } from '@afterpaytouch/portal-api'
import { compareObjects } from './object'

export const supportedLocales = ['en-AU', 'en-NZ', 'en-US', 'en-GB', 'en-CA', 'fr-CA', 'fr-FR', 'es-ES', 'it-IT'] as const

type Locale = (typeof supportedLocales)[number]

export const AddMoneyErrorMessages = {
  InvalidInput: 'Tried to add with no Money objects',
  CurrenciesMismatched: 'Tried to add mismatched currencies',
} as const

interface AddMoneyConfig {
  locale?: Locale | 'default'
  decimalPlaces?: number
  moneys: Money[]
}

/**
 *
 * @param {object} AddMoneyConfig an object with the Money variables you want to add together, as well as optional locale and decimal places for formatting
 * @returns {string} the total amount of the Money variables, as a string
 */
export const addMoneys = ({ locale = 'default', decimalPlaces = 2, moneys }: AddMoneyConfig): string => {
  validate(moneys)

  const moneysAsAmounts = moneys.map((m) => m.amount)
  const totalAmountAsString = moneysAsAmounts.reduce(sum, '0')
  const totalAmount = Number(totalAmountAsString) ?? 0

  const formatter = getFormatter(locale, decimalPlaces)
  const formattedAmount = formatter.format(totalAmount)

  return formattedAmount
}

const validate = (moneys: Money[]): void => {
  if (moneys.length === 0) {
    throw Error(AddMoneyErrorMessages.InvalidInput)
  }
  if (!isSameCurrencies(moneys)) {
    throw Error(AddMoneyErrorMessages.CurrenciesMismatched)
  }
}

const sum = (runningTotal: string, current: string): string => {
  const amount = parseFloat(runningTotal) + parseFloat(current)
  return amount.toString()
}

const isSameCurrencies = (moneys: Money[]): boolean => {
  const moneysAsCurrencies = moneys.map((m) => m.currency)
  const currencies = new Set(moneysAsCurrencies)
  const allSame = currencies.size === 1
  return allSame
}

const getFormatter = (locale: Locale | 'default', decimalPlaces: number): Intl.NumberFormat => {
  const formattingOptions = {
    ...(typeof decimalPlaces !== 'undefined' && {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }),
  }
  const formatter = new Intl.NumberFormat(locale, formattingOptions)
  return formatter
}

/**
 *
 * @param {array} moneys array of Money objects
 * @returns {number} the index of the highest Money object in the array
 */
export const getHighestAmountIndex = (moneys: Money[]): number => {
  if (moneys.length === 0) {
    return -1
  }

  let highestIndex = -1
  let highestNumber = 0

  moneys.every((val, idx) => {
    const currentAmt = Number(val.amount)

    if (isNaN(currentAmt) || val.amount.length === 0) {
      return false
    }

    if (currentAmt > highestNumber) {
      highestIndex = idx
      highestNumber = currentAmt
    }

    return true
  })

  return highestIndex
}

/**
 * @param {array} moneys array of Money objects
 * @returns {boolean} if true, all Money objects are same.
 */
export const allAmountsSame = (moneys: Money[]): boolean => {
  const [moneyReference] = moneys
  return moneys.length > 1 && moneys.every((money) => compareObjects(money, moneyReference))
}
